// Package imports:
import { useState } from "react"
import Bugsnag from '@bugsnag/js';
import ToastContainer from 'react-bootstrap/ToastContainer';
import Toast from 'react-bootstrap/Toast';
// Component imports:
import SearchConfirmModal from "../components/SearchConfirmModal/SearchConfirmModal";
import SearchConfirmModalUrl from "../components/SearchConfirmModal/SearchConfirmModalUrl";
import SearchConfirmModalWithReference from "../components/SearchConfirmModal/SearchConfirmModalWithReference";
import SearchConfirmModalWithReferenceUrl from "../components/SearchConfirmModal/SearchConfirmModalWithReferenceUrl";
import SearchConfirmModalWithReferenceAndBool from "../components/SearchConfirmModal/SearchConfirmModalWithReferenceAndBool";
import SearchConfirmModalWithReferenceAndBoolUrl from "../components/SearchConfirmModal/SearchConfirmModalWithReferenceAndBoolUrl";
import LoginConfirmModal from "../components/LoginConfirmModal/LoginConfirmModal";
import Alert, { AlertType } from "../ui-elements/Alert/Alert";
import Link from "../ui-elements/Link/Link";
// Service imports:
import { GET_KELDAN_API_URL, TOAST_DELAY } from "./config";
import { useStateRef } from "./hooks";
import { ErrorMessages } from "./errorMessages";

interface IToastSettings {
    headText: string,
    text?: JSX.Element | string
    type?: AlertType
}

interface ICustomSearchModalInfo {
    customHeader: string,
    customText: string | JSX.Element
}

type PrecookedToastMessage = 'no card' | 'bad request' | 'response not ok' | 'network error'
type ToastState = PrecookedToastMessage | IToastSettings | null;

type ConfirmPhase = 'confirm-modal' | 'confirm-modal-with-reference' | 'confirm-modal-with-reference-and-bonds' | 'confirm-modal-with-reference-and-addresses'
type Phase = 'initial' | ConfirmPhase | 'checking-if-expired' | 'login-modal'

export const useBuyingProcess = <T extends any>(
    onSuccessfulPurchase: (tryToBuyItem: T, modalReference: string, modalBool: boolean) => void,
    searchModalDataSource: string | ICustomSearchModalInfo,
    resetStateCallback: (() => void) = () => {}
) => {
    const [ tryToBuyItem, setTryToBuyItem ] = useState<T | null>(null);
    const [ phase, setPhase ] = useState<Phase>('initial');
    const [ toastError, setToastError ] = useState<ToastState>(null);

    // Optional states depending on type of search confirm modal.
    const [ modalReference, setModalReference ] = useState('');
    const modalReferenceRef = useStateRef(modalReference);
    const [ modalBool, setModalBool ] = useState(false);
    const modalBoolRef = useStateRef(modalBool);

    const resetState = (toastError: ToastState) => {
        setModalReference('');
        setTryToBuyItem(null);
        setPhase('initial');
        setToastError(toastError);
        resetStateCallback();
    }

    const purchase = () => {
        if (tryToBuyItem !== null) onSuccessfulPurchase(tryToBuyItem, modalReferenceRef.current, modalBoolRef.current);
        resetState(null);
    }

    const loginCheck = async () => {
        setPhase('checking-if-expired');
        try {
            const url = `${GET_KELDAN_API_URL() + '/Home/IsExpired'}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                }
            });
            if (response.ok) {
                const responseBody: {expired: boolean} = await response.json();
                const { expired } = responseBody;
                //engar niðurstöður fundust
                if (expired) setPhase('login-modal')
                else purchase();
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error')
        }
    }
    
    const tryPurchaseItem = (item: T, confirmPhase: ConfirmPhase = 'confirm-modal') => {
        setTryToBuyItem(item);
        setPhase(confirmPhase);
    }

    const getPrecookedAlert = (precookedToastMessage: PrecookedToastMessage) => {
        if (precookedToastMessage === 'no card') {
            return (
                <Alert
                    type='alert'
                    withCloseIcon
                    closeAlert={() => setToastError(null)}
                    headText='Greiðsluupplýsingar vantar'
                    text={<p>Kreditkortaupplýsingar eru ekki fullnægjandi. Vinsamlegast yfirfarið <Link href='/Min-Kelda/Stillingar' linkSize='16'>kortaupplýsingar</Link>.</p>}
                />
            );
        }
        return (
            <Alert
                type={(precookedToastMessage === 'network error')
                    ? 'error'
                    : 'alert'
                }
                withCloseIcon
                closeAlert={() => setToastError(null)}
                headText={(precookedToastMessage === 'bad request')
                    ? ErrorMessages.ErrorInRequest
                    : (precookedToastMessage === 'response not ok')
                    ? ErrorMessages.RequestFailed
                    : ErrorMessages.Other
                }
            />
        );
    }

    const displaySearchConfirmModal = () => (typeof searchModalDataSource === 'string')
        ? (
            <SearchConfirmModal
                show
                close={() => resetState(null)}
                submit={() => loginCheck()}
                dataSourceText={searchModalDataSource}
            />
        ) : (
            <SearchConfirmModal
                show
                close={() => resetState(null)}
                submit={() => loginCheck()}
                dataSourceText=''
                customHeader={searchModalDataSource.customHeader}
                customText={searchModalDataSource.customText}
            />
        );
    
    const displaySearchConfirmModalWithReference = () => (typeof searchModalDataSource === 'string')
        ? (
            <SearchConfirmModalWithReference
                show
                close={() => resetState(null)}
                submit={(ref) => {
                    setModalReference(ref)
                    loginCheck();
                }}
                dataSourceText={searchModalDataSource}
            />
        ) : (
            <SearchConfirmModalWithReference
                show
                close={() => resetState(null)}
                submit={(ref) => {
                    setModalReference(ref)
                    loginCheck();
                }}
                dataSourceText=''
                customHeader={searchModalDataSource.customHeader}
                customText={searchModalDataSource.customText}
            />
        );
    
    const displaySearchConfirmModalWithReferenceAndBonds = (type: 'bonds' | 'addresses') => (typeof searchModalDataSource === 'string')
        ? (
            <SearchConfirmModalWithReferenceAndBool
                show
                close={() => resetState(null)}
                type={type}
                submit={(ref, bonds) => {
                    setModalReference(ref)
                    setModalBool(bonds)
                    loginCheck();
                }}
                dataSourceText={searchModalDataSource}
            />
        ) : (
            <SearchConfirmModalWithReferenceAndBool
                show
                close={() => resetState(null)}
                type={type}
                submit={(ref) => {
                    setModalReference(ref)
                    setModalBool(modalBool)
                    loginCheck();
                }}
                dataSourceText=''
                customHeader={searchModalDataSource.customHeader}
                customText={searchModalDataSource.customText}
            />
        );
    
    const ModalsAndToasts: React.FC = () => (
        <div>
            {(phase === 'confirm-modal')
                ? displaySearchConfirmModal()
                : (phase === 'confirm-modal-with-reference')
                ? displaySearchConfirmModalWithReference()
                : (phase === 'confirm-modal-with-reference-and-bonds' || phase === 'confirm-modal-with-reference-and-addresses')
                ? displaySearchConfirmModalWithReferenceAndBonds(
                    (phase === 'confirm-modal-with-reference-and-bonds') ? 'bonds' : 'addresses'
                )
                : (phase === 'login-modal')
                ? (
                    <LoginConfirmModal
                        show
                        close={() => resetState(null)}
                        submit={() => purchase()}
                    />
                )
                : null
            }
            <ToastContainer className='toastContainer' >
                <Toast
                    show={(toastError !== null)}
                    autohide
                    delay={TOAST_DELAY}
                    onClose={() => setToastError(null)}
                >
                    {(toastError === null)
                    ? null
                    : (typeof toastError === 'string')
                    ? getPrecookedAlert(toastError)
                    : (
                        <Alert
                            type='alert'
                            withCloseIcon
                            closeAlert={() => setToastError(null)}
                            headText={toastError.headText}
                            text={toastError.text}
                        />
                    )}
                </Toast>
            </ToastContainer>
        </div>
    );


    return {
        isItemLoading: (item: T) => (item === tryToBuyItem),
        tryToBuyItem,
        tryPurchaseItem,
        resetState,
        setToastError,
        ModalsAndToasts
    }
}

export const useBuyingProcessUrl = <T extends any>(
    onPurchaseUrl: (tryToBuyItem: T, modalReference: string, modalBool: boolean) => string,
    onPurchaseCallback: (tryToBuyItem: T, modalReference: string, modalBool: boolean) => void,
    searchModalDataSource: string | ICustomSearchModalInfo,
    resetStateCallback: (() => void) = () => {}
) => {
    const [ tryToBuyItem, setTryToBuyItem ] = useState<T | null>(null);
    const [ phase, setPhase ] = useState<Phase>('initial');
    const [ afterLoginPhase, setAfterLoginPhase ] = useState<ConfirmPhase>('confirm-modal');
    const [ toastError, setToastError ] = useState<ToastState>(null);

    const resetState = (toastError: ToastState) => {
        setTryToBuyItem(null);
        setPhase('initial');
        setToastError(toastError);
        resetStateCallback();
    }

    const loginCheck = async (confirmPhase: ConfirmPhase) => {
        setPhase('checking-if-expired');
        try {
            const url = `${GET_KELDAN_API_URL() + '/Home/IsExpired'}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                }
            });
            if (response.ok) {
                const responseBody: {expired: boolean} = await response.json();
                const { expired } = responseBody;
                //engar niðurstöður fundust
                if (expired) {
                    setPhase('login-modal')
                    setAfterLoginPhase(confirmPhase);
                }
                else {
                    setPhase(confirmPhase);
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error')
        }
    }
    
    const tryPurchaseItem = (item: T, confirmPhase: ConfirmPhase = 'confirm-modal') => {
        setTryToBuyItem(item);
        loginCheck(confirmPhase);
    }

    const getPrecookedAlert = (precookedToastMessage: PrecookedToastMessage) => {
        if (precookedToastMessage === 'no card') {
            return (
                <Alert
                    type='alert'
                    withCloseIcon
                    closeAlert={() => setToastError(null)}
                    headText='Greiðsluupplýsingar vantar'
                    text={<p>Kreditkortaupplýsingar eru ekki fullnægjandi. Vinsamlegast yfirfarið <Link href='/Min-Kelda/Stillingar' linkSize='16'>kortaupplýsingar</Link>.</p>}
                />
            );
        }
        return (
            <Alert
                type={(precookedToastMessage === 'network error')
                    ? 'error'
                    : 'alert'
                }
                withCloseIcon
                closeAlert={() => setToastError(null)}
                headText={(precookedToastMessage === 'bad request')
                    ? ErrorMessages.ErrorInRequest
                    : (precookedToastMessage === 'response not ok')
                    ? ErrorMessages.RequestFailed
                    : ErrorMessages.Other
                }
            />
        );
    }

    const displaySearchConfirmModal = () => (typeof searchModalDataSource === 'string')
        ? (
            <SearchConfirmModalUrl
                show
                close={() => resetState(null)}
                onPurchaseUrl={() => {
                    if (tryToBuyItem) return onPurchaseUrl(tryToBuyItem, '', false);
                    else return '#';
                }}
                onPurchaseCallback={() => {
                    if (tryToBuyItem) onPurchaseCallback(tryToBuyItem, '', false);
                    resetState(null);
                }}
                dataSourceText={searchModalDataSource}
            />
        ) : (
            <SearchConfirmModalUrl
                show
                close={() => resetState(null)}
                onPurchaseUrl={() => {
                    if (tryToBuyItem) return onPurchaseUrl(tryToBuyItem, '', false);
                    else return '#';
                }}
                onPurchaseCallback={() => {
                    if (tryToBuyItem) onPurchaseCallback(tryToBuyItem, '', false);
                    resetState(null);
                }}
                dataSourceText=''
                customHeader={searchModalDataSource.customHeader}
                customText={searchModalDataSource.customText}
            />
        );
    
    const displaySearchConfirmModalWithReference = () => (typeof searchModalDataSource === 'string')
        ? (
            <SearchConfirmModalWithReferenceUrl
                show
                close={() => resetState(null)}
                onPurchaseUrl={(modalReference: string) => {
                    if (tryToBuyItem) return onPurchaseUrl(tryToBuyItem, modalReference, false);
                    else return '#';
                }}
                onPurchaseCallback={(modalReference: string) => {
                    if (tryToBuyItem) onPurchaseCallback(tryToBuyItem, modalReference, false);
                    resetState(null);
                }}
                dataSourceText={searchModalDataSource}
            />
        ) : (
            <SearchConfirmModalWithReferenceUrl
                show
                close={() => resetState(null)}
                onPurchaseUrl={(modalReference: string) => {
                    if (tryToBuyItem) return onPurchaseUrl(tryToBuyItem, modalReference, false);
                    else return '#';
                }}
                onPurchaseCallback={(modalReference: string) => {
                    if (tryToBuyItem) onPurchaseCallback(tryToBuyItem, modalReference, false);
                    resetState(null);
                }}
                dataSourceText=''
                customHeader={searchModalDataSource.customHeader}
                customText={searchModalDataSource.customText}
            />
        );
    
    const displaySearchConfirmModalWithReferenceAndBonds = (type: 'bonds' | 'addresses') => (typeof searchModalDataSource === 'string')
        ? (
            <SearchConfirmModalWithReferenceAndBoolUrl
                show
                close={() => resetState(null)}
                type={type}
                onPurchaseUrl={(modalReference: string, modalBool: boolean) => {
                    if (tryToBuyItem) return onPurchaseUrl(tryToBuyItem, modalReference, modalBool);
                    else return '#';
                }}
                onPurchaseCallback={(modalReference: string, modalBool: boolean) => {
                    if (tryToBuyItem) onPurchaseCallback(tryToBuyItem, modalReference, modalBool);
                    resetState(null);
                }}
                dataSourceText={searchModalDataSource}
            />
        ) : (
            <SearchConfirmModalWithReferenceAndBoolUrl
                show
                close={() => resetState(null)}
                type={type}
                onPurchaseUrl={(modalReference: string, modalBool: boolean) => {
                    if (tryToBuyItem) return onPurchaseUrl(tryToBuyItem, modalReference, modalBool);
                    else return '#';
                }}
                onPurchaseCallback={(modalReference: string, modalBool: boolean) => {
                    if (tryToBuyItem) onPurchaseCallback(tryToBuyItem, modalReference, modalBool);
                    resetState(null);
                }}
                dataSourceText=''
                customHeader={searchModalDataSource.customHeader}
                customText={searchModalDataSource.customText}
            />
        );
    
    const ModalsAndToasts: React.FC = () => (
        <div>
            {(phase === 'confirm-modal')
                ? displaySearchConfirmModal()
                : (phase === 'confirm-modal-with-reference')
                ? displaySearchConfirmModalWithReference()
                : (phase === 'confirm-modal-with-reference-and-bonds' || phase === 'confirm-modal-with-reference-and-addresses')
                ? displaySearchConfirmModalWithReferenceAndBonds(
                    (phase === 'confirm-modal-with-reference-and-bonds') ? 'bonds' : 'addresses'
                )
                : (phase === 'login-modal')
                ? (
                    <LoginConfirmModal
                        show
                        close={() => resetState(null)}
                        submit={() => setPhase(afterLoginPhase)}
                    />
                )
                : null
            }
            <ToastContainer className='toastContainer' >
                <Toast
                    show={(toastError !== null)}
                    autohide
                    delay={TOAST_DELAY}
                    onClose={() => setToastError(null)}
                >
                    {(toastError === null)
                    ? null
                    : (typeof toastError === 'string')
                    ? getPrecookedAlert(toastError)
                    : (
                        <Alert
                            type='alert'
                            withCloseIcon
                            closeAlert={() => setToastError(null)}
                            headText={toastError.headText}
                            text={toastError.text}
                        />
                    )}
                </Toast>
            </ToastContainer>
        </div>
    );


    return {
        isItemLoading: (item: T) => (item === tryToBuyItem),
        tryToBuyItem,
        tryPurchaseItem,
        resetState,
        setToastError,
        ModalsAndToasts
    }
}